import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components/keenicons';
import { LazyFormikSelect } from '@/components/form';
import { useAbility } from '@/providers/';
import { useMatterOptions } from '@/hooks';
import apiService from '@/services/apiService';
import letterTemplateService from '@/services/letters/letterTemplateService';
import { LetterEditor, MergeFieldPicker, fillInsIn, tokensIn } from '@/components/letter-editor';

const schema = Yup.object().shape({
  name: Yup.string().required('Name is required').max(255),
  record_type_id: Yup.string().nullable(),
  jurisdiction: Yup.string().nullable(),
  description: Yup.string().max(2000).nullable(),
  default_subject: Yup.string().max(255).nullable(),
  default_days_to_respond: Yup.number().min(1).max(365).required('Required'),
  is_active: Yup.boolean(),
});

/**
 * Create / edit a letter template: metadata, the body with merge chips and
 * fill-in prompts, and a preview against a real matter.
 */
const LetterTemplateEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { ability } = useAbility();
  const editorRef = useRef(null);
  const loadMatterOptions = useMatterOptions({ defaultPageSize: 20 });

  const canManage = ability.can('handle', 'letter-template');
  const [template, setTemplate] = useState(null);
  const [body, setBody] = useState('');
  const [recordTypes, setRecordTypes] = useState([]);
  const [recordTypeId, setRecordTypeId] = useState('');
  const [catalog, setCatalog] = useState({ groups: [], jurisdictions: [] });
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    apiService.get('/record-types')
      .then((res) => setRecordTypes(res?.data?.data || []))
      .catch(() => setRecordTypes([]));
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    letterTemplateService.get(id)
      .then(({ data }) => {
        if (cancelled) return;
        setTemplate(data);
        setBody(data.body || '');
        setRecordTypeId(data.record_type?.id || '');
      })
      .catch((error) => {
        console.error('Error', error);
        toast.error('Could not load the template.');
        navigate('/documents/letter-templates');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    letterTemplateService.mergeFields(recordTypeId || null)
      .then((res) => setCatalog({ groups: res?.data?.groups || [], jurisdictions: res?.data?.jurisdictions || [] }))
      .catch(() => setCatalog({ groups: [], jurisdictions: [] }));
  }, [recordTypeId]);

  const initialValues = {
    name: template?.name || '',
    record_type_id: template?.record_type?.id || '',
    jurisdiction: template?.jurisdiction || '',
    description: template?.description || '',
    default_subject: template?.default_subject || '',
    default_days_to_respond: template?.default_days_to_respond || 30,
    is_active: template ? !!template.is_active : true,
    preview_matter_id: '',
  };

  const handleSubmit = async (values) => {
    if (!body || body === '<p></p>') {
      toast.error('The template body is empty.');
      return;
    }
    setSaving(true);
    const payload = {
      name: values.name,
      record_type_id: values.record_type_id || null,
      jurisdiction: values.jurisdiction || null,
      description: values.description || null,
      default_subject: values.default_subject || null,
      default_days_to_respond: values.default_days_to_respond,
      is_active: values.is_active,
      body,
    };
    try {
      const res = id
        ? await letterTemplateService.update(id, payload)
        : await letterTemplateService.create(payload);
      if (res?.status) {
        toast.success(id ? 'Template saved.' : 'Template created.');
        if (!id) navigate(`/documents/letter-templates/${res.data.id}`);
        else setTemplate(res.data);
      }
    } catch (error) {
      console.error('Error', error);
      const detail = error?.errors ? Object.values(error.errors).flat().join(' ') : error?.message;
      toast.error('Could not save the template.', { description: detail || 'Please check the fields.' });
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async (matterId) => {
    if (!id) {
      toast.info('Save the template first, then preview it against a matter.');
      return;
    }
    if (!matterId) {
      toast.info('Pick a matter to preview against.');
      return;
    }
    setPreviewLoading(true);
    try {
      const { data } = await letterTemplateService.preview(id, { matter_id: matterId });
      setPreview(data);
    } catch (error) {
      console.error('Error', error);
      toast.error('Preview failed.', { description: error?.message });
    } finally {
      setPreviewLoading(false);
    }
  };

  const tokenCount = tokensIn(body).length;
  const fillInCount = fillInsIn(body).length;

  if (loading) return <Container><div className="card p-6 text-sm text-gray-500">Loading template…</div></Container>;

  return (
    <Container>
      <Formik enableReinitialize initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit}>
        {({ values, setFieldValue }) => (
          <Form>
            <div className="card mb-4">
              <div className="card-header">
                <div className="flex items-center gap-2">
                  <button type="button" className="btn btn-sm btn-icon btn-light" onClick={() => navigate('/documents/letter-templates')} title="Back">
                    <KeenIcon icon="black-left" />
                  </button>
                  <h3 className="card-title">{id ? (canManage ? 'Edit template' : 'Template') : 'New template'}</h3>
                  {template?.is_seeded && <span className="badge badge-sm badge-outline badge-light">Starter template</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{tokenCount} merge field{tokenCount === 1 ? '' : 's'} · {fillInCount} fill-in{fillInCount === 1 ? '' : 's'}</span>
                  {canManage && (
                    <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                      {saving ? 'Saving…' : 'Save template'}
                    </button>
                  )}
                </div>
              </div>

              <div className="card-body grid md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="form-label mb-1">Name <span className="text-red-600">*</span></label>
                  <Field name="name" className="input" disabled={!canManage} />
                  <ErrorMessage name="name" component="div" className="text-red-500 text-xs mt-1" />
                </div>
                <div>
                  <label className="form-label mb-1">Practice area</label>
                  <Field
                    as="select"
                    name="record_type_id"
                    className="select"
                    disabled={!canManage}
                    onChange={(e) => { setFieldValue('record_type_id', e.target.value); setRecordTypeId(e.target.value); }}
                  >
                    <option value="">Any matter (global fields only)</option>
                    {recordTypes.map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                  </Field>
                </div>
                <div>
                  <label className="form-label mb-1">Jurisdiction</label>
                  <Field as="select" name="jurisdiction" className="select" disabled={!canManage}>
                    <option value="">None (no statutory checks)</option>
                    {catalog.jurisdictions.map((s) => <option key={s.code} value={s.code}>{s.name}{s.statute ? ` (${s.statute})` : ''}</option>)}
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <label className="form-label mb-1">Default email subject <span className="text-gray-400 text-xs">(merge fields allowed)</span></label>
                  <Field name="default_subject" className="input" placeholder="Time-Limited Policy Limits Demand – {{client.full_name}}" disabled={!canManage} />
                </div>
                <div>
                  <label className="form-label mb-1">Default days to respond</label>
                  <Field name="default_days_to_respond" type="number" min={1} max={365} className="input" disabled={!canManage} />
                  <ErrorMessage name="default_days_to_respond" component="div" className="text-red-500 text-xs mt-1" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm mb-2">
                    <Field type="checkbox" name="is_active" className="checkbox checkbox-sm" disabled={!canManage} /> Active
                  </label>
                </div>
                <div className="md:col-span-4">
                  <label className="form-label mb-1">Description</label>
                  <Field name="description" className="input" placeholder="When to use this template" disabled={!canManage} />
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-4 gap-4">
              <div className="lg:col-span-3">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title text-sm">Body</h3>
                    <span className="text-xs text-gray-500">Click a merge field on the right to insert it. Use "+ Fill-in" for text the attorney writes per case.</span>
                  </div>
                  <div className="card-body">
                    <LetterEditor ref={editorRef} value={body} onChange={setBody} readOnly={!canManage} mode="template" />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <MergeFieldPicker
                  groups={catalog.groups}
                  disabled={!canManage}
                  onInsert={(token, label) => editorRef.current?.insertToken(token, label)}
                />

                <div className="card">
                  <div className="card-header"><h3 className="card-title text-sm">Preview against a matter</h3></div>
                  <div className="card-body">
                    <LazyFormikSelect name="preview_matter_id" placeholder="Search matters…" loadOptions={loadMatterOptions} pageSize={20} />
                    <button
                      type="button"
                      className="btn btn-sm btn-light w-full mt-3"
                      disabled={previewLoading || !id}
                      onClick={() => runPreview(values.preview_matter_id)}
                    >
                      <KeenIcon icon="eye" className="btn-icon" /> {previewLoading ? 'Rendering…' : 'Preview'}
                    </button>
                    {!id && <p className="text-xs text-gray-500 mt-2">Save first to preview.</p>}
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {preview && (
        <div className="modal-wrapper modal-animate-fade">
          <div className="modal-animate-scale modal-body w-[95%] max-w-4xl max-h-[92vh] overflow-y-auto relative">
            <h3 className="card-title mb-3">Preview</h3>
            <button type="button" onClick={() => setPreview(null)} className="modal-close-btn"><KeenIcon icon="cross" /></button>
            {(Object.keys(preview.missing || {}).length > 0 || (preview.fill_ins || []).length > 0) && (
              <div className="p-3 mb-3 rounded-md bg-yellow-50 border border-yellow-200 text-xs text-yellow-900">
                {Object.keys(preview.missing || {}).length > 0 && (
                  <div><strong>This matter is missing:</strong> {Object.values(preview.missing).join(', ')}</div>
                )}
                {(preview.fill_ins || []).length > 0 && (
                  <div><strong>Fill-ins the drafter will write:</strong> {preview.fill_ins.join('; ')}</div>
                )}
              </div>
            )}
            <div className="letter-preview prose max-w-none border border-gray-200 rounded-md p-8 bg-white" dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
        </div>
      )}
    </Container>
  );
};

export { LetterTemplateEditor };
